import { FieldErrorCode, validateDisplayName, validateUsername } from '@neosocial/protocol';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { tr, useT } from '../i18n';
import { api, ApiRequestError } from '../lib/api';
import { flagOn } from '../store/config';
import { updateData, useData } from '../store/data';
import { signOut } from '../store/session';
import { setUnsavedGuard } from '../store/ui';
import { blobToDataUri, IMAGE_ACCEPT, imageErrorText, looksLikeImage } from '../lib/image';
import { ImageEditor } from '../ui/ImageEditor';
import { Avatar, Btn } from '../ui/kit';
import { confirmDialog, Dialog, toast } from '../ui/overlay';
import { reportError } from '../hub/social';
import { UnsavedBar } from './Layer';
import { PasswordInput } from '../auth/PasswordInput';
import { SetGroup, SetRow } from './Settings';

/**
 * Choosing a new @username: the name (checked as you type) and the
 * password, which the server asks for. Used from Settings › Profile and,
 * when staff reset the name to a placeholder, at once (`MustRenameGate`).
 */
function UsernameForm({ onDone, onCancel }: { onDone(): void; onCancel?: () => void }) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ name?: string; password?: string; form?: string }>({});
  const check = name ? validateUsername(name) : null;
  const nameErr = err.name ?? (check && !check.ok ? t('profile.username.invalid') : undefined);
  const ready = !!check?.ok && check.value !== me?.username && !!password && !busy;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || !check?.ok) return;
    setBusy(true);
    setErr({});
    try {
      const u = await api.me.patch({ username: check.value, password });
      updateData((d) => ({ ...d, me: u, users: { ...d.users, [u.id]: u } }));
      toast(t('profile.username.done', { name: u.username }), { icon: 'check', tone: 'ok' });
      onDone();
    } catch (er) {
      const f = er instanceof ApiRequestError ? er.fieldCodes() : {};
      if (f.password) setErr({ password: t('ns.pw.wrong') });
      else if (f.username === FieldErrorCode.USERNAME_TAKEN)
        setErr({ name: t('profile.username.taken') });
      else if (f.username) setErr({ name: t('profile.username.invalid') });
      else if (er instanceof ApiRequestError && er.status === 429)
        setErr({
          form: t('profile.username.wait', {
            n: Math.max(1, Math.ceil((er.retryAfter ?? 86_400) / 86_400)),
          }),
        });
      else reportError(er);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ns-form ns-username-form" onSubmit={(e) => void submit(e)} noValidate>
      <label className="ns-field">
        <span className="ns-label">{t('profile.username.label')}</span>
        <input
          className="nx-input"
          name="handle"
          dir="ltr"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          value={name}
          placeholder={me?.username}
          aria-invalid={!!nameErr || undefined}
          onChange={(e) => (setName(e.target.value.toLowerCase()), setErr({}))}
          data-autofocus
        />
        {nameErr ? (
          <span className="ns-err" role="alert">
            {nameErr}
          </span>
        ) : (
          <span className="ns-help">{t('profile.username.help')}</span>
        )}
      </label>
      <label className="ns-field">
        <span className="ns-label">{t('auth.password.label')}</span>
        <PasswordInput
          autoComplete="current-password"
          value={password}
          aria-invalid={!!err.password || undefined}
          onChange={(e) => (setPassword(e.target.value), setErr({}))}
        />
        {err.password ? (
          <span className="ns-err" role="alert">
            {err.password}
          </span>
        ) : null}
      </label>
      {err.form ? (
        <p className="ns-err" role="alert">
          {err.form}
        </p>
      ) : null}
      <div className="ns-row-btns end">
        {onCancel ? (
          <Btn size="sm" kind="ghost" onClick={onCancel}>
            {t('c.cancel')}
          </Btn>
        ) : (
          <Btn size="sm" kind="quiet" icon="power" onClick={() => void signOut()}>
            {t('ns.signout')}
          </Btn>
        )}
        <Btn kind="primary" size="sm" type="submit" disabled={!ready} busy={busy}>
          {t('profile.username.save')}
        </Btn>
      </div>
    </form>
  );
}

/** Settings › Profile: the @username, with Change. */
function UsernameGroup() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const [open, setOpen] = useState(false);
  if (!me) return null;
  return (
    <SetGroup title={t('profile.username.label')}>
      <SetRow label={`@${me.username}`} sub={t('profile.username.help')}>
        <Btn size="sm" onClick={() => setOpen(true)}>
          {t('profile.username.change')}
        </Btn>
      </SetRow>
      {open ? (
        <Dialog
          title={t('profile.username.title')}
          icon="at"
          tone="mode"
          width={440}
          cls="ns-username-dlg"
          onClose={() => setOpen(false)}
        >
          <UsernameForm onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
        </Dialog>
      ) : null}
    </SetGroup>
  );
}

/**
 * Staff reset the username to a placeholder (`nx_must_rename`): choose a
 * new one before going on. Only signing out is the other way out. Shown by
 * shell/StaffNotices (after any staff warning); it names the stand-in
 * (console CO-BUG-12).
 */
export function MustRenameGate() {
  const { t } = useT();
  const must = useData((d) => !!d.me?.nx_must_rename);
  const current = useData((d) => d.me?.username ?? '');
  if (!must) return null;
  return (
    <Dialog
      title={t('profile.username.mustTitle')}
      icon="at"
      tone="mode"
      width={440}
      dismissable={false}
      onClose={() => {}}
      cls="ns-must-rename"
    >
      <p dir="auto">{t('cfg.rename.body', { name: current })}</p>
      <UsernameForm onDone={() => {}} />
    </Dialog>
  );
}

export function ProfileSection() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const custom = useData((d) => d.settings?.custom_status?.text ?? '');
  const [name, setName] = useState(me?.global_name ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [pronouns, setPronouns] = useState(me?.pronouns ?? '');
  const [status, setStatus] = useState(custom);
  const [avatar, setAvatar] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // The picked image, open in the crop editor (any size: it is shrunk there).
  const [editing, setEditing] = useState<File | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const dirty =
    !!me &&
    (name !== (me.global_name ?? '') ||
      bio !== (me.bio ?? '') ||
      pronouns !== (me.pronouns ?? '') ||
      avatar !== undefined ||
      status !== custom);
  // leaving the page (another tab, close, Escape) with unsaved changes asks first
  useEffect(() => {
    if (!dirty) return;
    return setUnsavedGuard(() => {
      const { t: tt } = tr();
      return confirmDialog({
        title: tt('set.unsaved.title'),
        body: tt('set.unsaved.body'),
        confirmLabel: tt('set.unsaved.discard'),
        danger: true,
      });
    });
  }, [dirty]);
  if (!me) return null;
  const nameCheck = name.trim() ? validateDisplayName(name) : null;
  const nameErr = nameCheck && !nameCheck.ok ? t(`ns.dn.${nameCheck.error}`) : null;
  const preview = avatar === undefined ? me : { ...me, avatar };
  const reset = () => {
    setName(me.global_name ?? '');
    setBio(me.bio ?? '');
    setPronouns(me.pronouns ?? '');
    setStatus(custom);
    setAvatar(undefined);
  };
  const save = async () => {
    setBusy(true);
    try {
      const u = await api.me.patch({
        global_name: name.trim() || null,
        bio,
        pronouns,
        ...(avatar !== undefined ? { avatar } : {}),
      });
      updateData((d) => ({ ...d, me: u, users: { ...d.users, [u.id]: u } }));
      // the fields now show what was saved (trimmed), so nothing reads as unsaved
      setName(u.global_name ?? '');
      setBio(u.bio ?? '');
      setPronouns(u.pronouns ?? '');
      if (status !== custom) {
        const s = await api.me.patchSettings({
          custom_status: status.trim()
            ? { text: status.trim(), emoji_name: null, expires_at: null }
            : null,
        });
        updateData((d) => ({ ...d, settings: s }));
        setStatus(s.custom_status?.text ?? '');
      }
      setAvatar(undefined);
      toast(t('ns.saved'), { icon: 'check', tone: 'ok' });
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h2>{t('ns.set.profile')}</h2>
      <div className="ns-prof-edit nx-card">
        <Avatar user={preview} size={72} />
        <div className="ns-prof-edit-t">
          <b dir="auto">{name.trim() || me.username}</b>
          <small dir="ltr">@{me.username}</small>
          <div className="ns-row-btns">
            <Btn
              size="sm"
              icon="upload"
              onClick={() =>
                // the console's Image uploads covers avatars too (the server refuses one then)
                flagOn('image_uploads')
                  ? file.current?.click()
                  : toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' })
              }
            >
              {t('ns.prof.avatar')}
            </Btn>
            {(avatar === undefined ? me.avatar : avatar) ? (
              <Btn size="sm" kind="quiet" onClick={() => setAvatar(null)}>
                {t('ns.prof.removeAvatar')}
              </Btn>
            ) : null}
          </div>
          <input
            ref={file}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (!looksLikeImage(f)) {
                toast(t('img.err.unsupported'), { tone: 'warn', icon: 'image' });
                return;
              }
              setEditing(f);
            }}
          />
        </div>
      </div>
      {editing ? (
        <ImageEditor
          file={editing}
          purpose="avatar"
          onCancel={() => setEditing(null)}
          onDone={(img) => {
            setEditing(null);
            blobToDataUri(img.blob).then(setAvatar, (e: unknown) =>
              toast(imageErrorText(t, e), { tone: 'warn', icon: 'image' }),
            );
          }}
        />
      ) : null}
      <SetGroup form>
        <div className="nx-set-form">
          <label className="ns-field">
            <span className="ns-label">{t('auth.displayName.label')}</span>
            <input
              className="nx-input"
              dir="auto"
              value={name}
              maxLength={128}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!nameErr || undefined}
            />
            <span className={nameErr ? 'ns-err' : 'ns-help'}>
              {nameErr ?? t('auth.displayName.help')}
            </span>
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('ns.prof.status')}</span>
            <input
              className="nx-input"
              dir="auto"
              value={status}
              maxLength={128}
              placeholder={t('pres.custom')}
              onChange={(e) => setStatus(e.target.value)}
            />
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('ns.prof.pronouns')}</span>
            <input
              className="nx-input"
              dir="auto"
              value={pronouns}
              maxLength={40}
              onChange={(e) => setPronouns(e.target.value)}
            />
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('hub.prof.about')}</span>
            <textarea
              className="nx-textarea"
              dir="auto"
              rows={3}
              value={bio}
              maxLength={190}
              onChange={(e) => setBio(e.target.value)}
            />
            <span className="ns-help">{t('ns.prof.bioLeft', { n: 190 - bio.length })}</span>
          </label>
        </div>
      </SetGroup>
      <UsernameGroup />
      {/* unsaved edits: the sticky bar (§7.24), so Save is in reach on any window */}
      {dirty ? (
        <UnsavedBar
          onReset={reset}
          busy={busy}
          saveDisabled={!!nameErr}
          onSave={() => void save()}
        />
      ) : null}
    </>
  );
}
